import { cookiePreferencesPortal } from "@design-system/atoms";
import { Button, Card, CardBody } from "@design-system/components";
import { Portal } from "@design-system/components/Portal";
import { trans } from "@mongez/localization";
import { useEffect, useState } from "react";
import { CookieConsent } from "./CookieConsentBanner";

interface CookiePreferencesModalProps {
  onSave: (consent: CookieConsent) => void;
  onClose: () => void;
}

export function CookiePreferencesModal({
  onSave,
  onClose,
}: CookiePreferencesModalProps) {
  const [preferences, setPreferences] = useState<CookieConsent>({
    essential: true,
    analytics: false,
    functional: false,
    marketing: false,
  });

  useEffect(() => {
    // Load existing preferences
    const savedConsent = localStorage.getItem("cookie_consent");
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        setPreferences(parsed);
      } catch (error) {
        console.error("Error parsing saved cookie consent:", error);
      }
    }
  }, []);

  const handleToggle = (category: keyof CookieConsent) => {
    if (category === "essential") return; // Essential cookies cannot be disabled

    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    onSave(preferences);
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted: CookieConsent = {
      essential: true,
      analytics: true,
      functional: true,
      marketing: true,
    };
    onSave(allAccepted);
    onClose();
  };

  const handleRejectAll = () => {
    const onlyEssential: CookieConsent = {
      essential: true,
      analytics: false,
      functional: false,
      marketing: false,
    };
    onSave(onlyEssential);
    onClose();
  };

  return (
    <Portal
      portal={cookiePreferencesPortal}
      classes={{
        content: "max-w-4xl w-full max-h-[90vh] overflow-y-auto",
        overlay: "bg-black bg-opacity-70 backdrop-blur-sm",
      }}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 pb-4">
          <div>
            <h2 className="text-3xl font-bold text-primary">
              {trans("cookiePreferences.title")}
            </h2>
            <p className="mt-2 text-gray-600">
              {trans("cookiePreferences.description")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 transition-colors hover:text-gray-600"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Cookie Categories */}
        <div className="space-y-4">
          {/* Essential Cookies */}
          <Card className="border-l-4 border-l-green-500">
            <CardBody>
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {trans("cookiePreferences.categories.essential.title")}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {trans("cookiePreferences.categories.essential.subtitle")}
                  </p>
                </div>
                <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
                  {trans("cookiePreferences.categories.essential.status")}
                </span>
              </div>
              <p className="text-sm text-gray-700">
                {trans("cookiePreferences.categories.essential.description")}
              </p>
            </CardBody>
          </Card>

          {/* Analytics Cookies */}
          <Card className="border-l-4 border-l-blue-500">
            <CardBody>
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {trans("cookiePreferences.categories.analytics.title")}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {trans("cookiePreferences.categories.analytics.subtitle")}
                  </p>
                </div>
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={() => handleToggle("analytics")}
                    className="peer sr-only"
                  />
                  <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-bright-orange peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300"></div>
                </label>
              </div>
              <p className="text-sm text-gray-700">
                {trans("cookiePreferences.categories.analytics.description")}
              </p>
            </CardBody>
          </Card>

          {/* Functional Cookies */}
          <Card className="border-l-4 border-l-purple-500">
            <CardBody>
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {trans("cookiePreferences.categories.functional.title")}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {trans("cookiePreferences.categories.functional.subtitle")}
                  </p>
                </div>
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={preferences.functional}
                    onChange={() => handleToggle("functional")}
                    className="peer sr-only"
                  />
                  <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-bright-orange peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300"></div>
                </label>
              </div>
              <p className="text-sm text-gray-700">
                {trans("cookiePreferences.categories.functional.description")}
              </p>
            </CardBody>
          </Card>

          {/* Marketing Cookies */}
          <Card className="border-l-4 border-l-orange-500">
            <CardBody>
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {trans("cookiePreferences.categories.marketing.title")}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {trans("cookiePreferences.categories.marketing.subtitle")}
                  </p>
                </div>
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={() => handleToggle("marketing")}
                    className="peer sr-only"
                  />
                  <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-bright-orange peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300"></div>
                </label>
              </div>
              <p className="text-sm text-gray-700">
                {trans("cookiePreferences.categories.marketing.description")}
              </p>
            </CardBody>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 border-t border-gray-200 pt-6 sm:flex-row">
          <Button
            variant="outline"
            onClick={handleRejectAll}
            className="flex-1"
          >
            {trans("cookiePreferences.buttons.rejectAll")}
          </Button>
          <Button
            variant="outline"
            onClick={handleAcceptAll}
            className="flex-1"
          >
            {trans("cookiePreferences.buttons.acceptAll")}
          </Button>
          <Button
            onClick={handleSave}
            className="flex-1 bg-bright-orange text-white hover:bg-orange-600"
          >
            {trans("cookiePreferences.buttons.savePreferences")}
          </Button>
        </div>

        {/* Footer Note */}
        <div className="pt-4 text-center">
          <p className="text-sm text-gray-600">
            {trans("cookiePreferences.footerNote")}
          </p>
        </div>
      </div>
    </Portal>
  );
}
